import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import AnaliseCreditoDialog from "@/components/AnaliseCreditoDialog";
import AnaliseCreditoAuto from "@/components/AnaliseCreditoAuto";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Edit,
  Trash2,
  User,
  Phone,
  PhoneOff,
  Mail,
  MapPin,
  Briefcase,
  Wallet,
  CreditCard,
  Calendar,
  Clock,
  Hash,
  Building2,
  Loader2,
  FileText,
  MessageSquare,
  MessageCircle,
  Star,
  Landmark,
  ArrowRightLeft,
  RefreshCw,
  TrendingDown
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function ClienteDetalhes() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contratos, setContratos] = useState([]);
  const [loadingContratos, setLoadingContratos] = useState(false);
  const [showPortabilidade, setShowPortabilidade] = useState({});
  const [showRefin, setShowRefin] = useState({});

  // Função para formatar CPF no padrão 000.111.222-33
  const formatCPF = (cpf) => {
    if (!cpf) return "-";
    const numbers = cpf.replace(/\D/g, "");
    if (numbers.length !== 11) return cpf;
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  useEffect(() => {
    fetchCliente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCliente = async () => {
    try {
      const response = await axios.get(`${API}/clientes/${id}`);
      setCliente(response.data);
    } catch (error) {
      console.error("Erro ao carregar cliente:", error);
      toast.error("Cliente não encontrado");
      navigate("/clientes");
    } finally {
      setLoading(false);
    }
  };

  const fetchContratos = async () => {
    try {
      setLoadingContratos(true);
      const response = await axios.get(`${API}/contratos?cliente_id=${id}`);
      setContratos(response.data.contratos || []);
    } catch (error) {
      console.error("Erro ao carregar contratos:", error);
      setContratos([]);
    } finally {
      setLoadingContratos(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clientes/${id}`);
      toast.success("Cliente excluído com sucesso");
      navigate("/clientes");
    } catch (error) {
      console.error("Erro ao excluir cliente:", error);
      toast.error("Erro ao excluir cliente");
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      ATIVO: "bg-green-100 text-green-800",
      INATIVO: "bg-red-100 text-red-800",
      REPROVADO: "bg-red-100 text-red-800",
      BLOQUEADO: "bg-gray-100 text-gray-800",
      "EM PROSPECÇÃO": "bg-yellow-100 text-yellow-800",
    };
    return (
      <Badge className={cn("font-medium text-sm", statusColors[status] || "bg-gray-100 text-gray-800")}>
        {status}
      </Badge>
    );
  };

  const getEsteiraBadge = (esteira) => {
    const esteiraColors = {
      CAPTACAO: "bg-blue-100 text-blue-800",
      EM_SIMULACAO: "bg-purple-100 text-purple-800",
      SEM_INTERESSE: "bg-slate-100 text-slate-800",
      MARGEM_NEGATIVA: "bg-red-100 text-red-800",
      COM_VALORES: "bg-emerald-100 text-emerald-800",
      SEM_VALORES: "bg-amber-100 text-amber-800",
      PROPOSTA_EM_ANALISE: "bg-orange-100 text-orange-800",
      PROPOSTA_APROVADO: "bg-green-100 text-green-800",
      PROPOSTA_REPROVADO: "bg-red-100 text-red-800",
      FINALIZADO: "bg-gray-100 text-gray-800",
      // Legados (compatibilidade)
      QUALIFICACAO: "bg-purple-100 text-purple-800",
      EM_ATENDIMENTO: "bg-purple-100 text-purple-800",
      ANALISE_DOCUMENTAL: "bg-orange-100 text-orange-800",
      APROVADO: "bg-green-100 text-green-800",
      RECUSADO: "bg-red-100 text-red-800",
    };
    return (
      <Badge variant="outline" className={cn("font-medium", esteiraColors[esteira] || "bg-gray-100")}>
        {esteira?.replace(/_/g, " ") || "N/A"}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!cliente) {
    return null;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/clientes">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={cliente.foto_url || ""} />
              <AvatarFallback className="bg-primary/10 text-primary text-xl">
                {cliente.nome?.charAt(0)?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900" data-testid="cliente-nome">
                  {cliente.nome}
                </h1>
                {getStatusBadge(cliente.acoes_operacionais?.status || "ATIVO")}
              </div>
              <div className="flex items-center gap-4 text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Hash className="h-4 w-4" />
                  {cliente.codigo}
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {formatCPF(cliente.cpf)}
                </span>
                {getEsteiraBadge(cliente.esteira_atual)}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AnaliseCreditoAuto clienteId={id} />
          <AnaliseCreditoDialog prefill={{ dataNascimento: cliente.data_nascimento, vinculo: cliente.vinculo, orgaoNome: cliente.orgao_nome }} />
          <Link to={`/clientes/${id}/editar`}>
            <Button variant="outline" className="gap-2" data-testid="edit-client-btn">
              <Edit className="h-4 w-4" />
              Editar
            </Button>
          </Link>
          <Button
            variant="destructive"
            className="gap-2"
            onClick={() => setDeleteDialogOpen(true)}
            data-testid="delete-client-btn"
          >
            <Trash2 className="h-4 w-4" />
            Excluir
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cliente desde</p>
                <p className="font-semibold">{formatDate(cliente.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Clock className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Última alteração</p>
                <p className="font-semibold">{formatDateTime(cliente.updated_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Classificação</p>
                <p className="font-semibold">{cliente.classificacao}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Última operação</p>
                <p className="font-semibold">{cliente.ultima_operacao || "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs with Details */}
      <Tabs defaultValue="pessoal" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pessoal" className="gap-2">
            <User className="h-4 w-4" />
            Dados Pessoais
          </TabsTrigger>
          <TabsTrigger value="contato" className="gap-2">
            <Phone className="h-4 w-4" />
            Contato
          </TabsTrigger>
          <TabsTrigger value="profissional" className="gap-2">
            <Briefcase className="h-4 w-4" />
            Profissional
          </TabsTrigger>
          <TabsTrigger value="bancario" className="gap-2">
            <CreditCard className="h-4 w-4" />
            Bancário
          </TabsTrigger>
          <TabsTrigger value="contratos" className="gap-2" onClick={() => fetchContratos()}>
            <Landmark className="h-4 w-4" />
            Monitoramento Contrato
          </TabsTrigger>
        </TabsList>

        {/* Tab: Dados Pessoais */}
        <TabsContent value="pessoal">
          <Card>
            <CardHeader>
              <CardTitle>Dados Pessoais</CardTitle>
              <CardDescription>Informações de identificação do cliente</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <p className="text-sm text-muted-foreground">Nome Completo</p>
                  <p className="font-medium">{cliente.nome}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gosta de ser chamado</p>
                  <p className="font-medium">{cliente.apelido || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">CPF</p>
                  <p className="font-medium font-mono">{formatCPF(cliente.cpf)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">RG</p>
                  <p className="font-medium">{cliente.rg || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Órgão Expedidor</p>
                  <p className="font-medium">{cliente.orgao_expedidor || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data de Emissão</p>
                  <p className="font-medium">{formatDate(cliente.data_emissao_rg)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data de Nascimento</p>
                  <p className="font-medium">{formatDate(cliente.data_nascimento)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Naturalidade</p>
                  <p className="font-medium">{cliente.naturalidade || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nacionalidade</p>
                  <p className="font-medium">{cliente.nacionalidade}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sexo</p>
                  <p className="font-medium">{cliente.sexo}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado Civil</p>
                  <p className="font-medium">{cliente.estado_civil}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Assina</p>
                  <p className="font-medium">{cliente.assina}</p>
                </div>

                <Separator className="col-span-full" />

                <div>
                  <p className="text-sm text-muted-foreground">Nome do Pai</p>
                  <p className="font-medium">{cliente.nome_pai || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nome da Mãe</p>
                  <p className="font-medium">{cliente.nome_mae || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nome do Cônjuge</p>
                  <p className="font-medium">{cliente.nome_conjuge || "-"}</p>
                </div>

                <Separator className="col-span-full" />

                <div>
                  <p className="text-sm text-muted-foreground">Origem dos Dados</p>
                  <p className="font-medium">{cliente.origem_dados || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">1ª Instituição</p>
                  <p className="font-medium">
                    {typeof cliente.primeira_instituicao === 'object' 
                      ? (cliente.primeira_instituicao?.nome || "-") 
                      : (cliente.primeira_instituicao || "-")}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">2ª Instituição</p>
                  <p className="font-medium">
                    {typeof cliente.segunda_instituicao === 'object' 
                      ? (cliente.segunda_instituicao?.nome || "-") 
                      : (cliente.segunda_instituicao || "-")}
                  </p>
                </div>
              </div>

              {/* Endereço */}
              {cliente.endereco && (
                <>
                  <Separator className="my-6" />
                  <div>
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Endereço
                    </h4>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                      <div className="lg:col-span-2">
                        <p className="text-sm text-muted-foreground">Logradouro</p>
                        <p className="font-medium">
                          {cliente.endereco.logradouro}
                          {cliente.endereco.numero && `, ${cliente.endereco.numero}`}
                          {cliente.endereco.complemento && ` - ${cliente.endereco.complemento}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Bairro</p>
                        <p className="font-medium">{cliente.endereco.bairro || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">CEP</p>
                        <p className="font-medium font-mono">{cliente.endereco.cep || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Cidade</p>
                        <p className="font-medium">{cliente.endereco.cidade || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Estado</p>
                        <p className="font-medium">{cliente.endereco.estado || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Tipo de Residência</p>
                        <p className="font-medium">{cliente.endereco.tipo_residencia || "-"}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Contato */}
        <TabsContent value="contato">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Telefones - Layout Compacto */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Telefones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                {cliente.telefones?.length > 0 ? (
                  cliente.telefones.map((tel, index) => (
                    <div key={tel.id || index} className="border-b last:border-b-0 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-52">
                          <p className="text-sm font-semibold text-slate-600 mb-2">Tel. {index + 1}</p>
                          <p className={cn(
                            "text-xl font-mono px-3 py-2 bg-slate-50 rounded-md border",
                            tel.bloqueado && "text-red-500 border-red-300 bg-red-50"
                          )}>
                            {tel.numero || "-"}
                          </p>
                        </div>
                        {tel.observacao && (
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-600 mb-2">Obs.:</p>
                            <p className="text-base px-3 py-2 bg-slate-50 rounded-md border">{tel.observacao}</p>
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-6">
                          {/* Botão Principal */}
                          <div className={cn(
                            "h-12 w-12 rounded-full flex items-center justify-center shadow-md",
                            tel.principal 
                              ? "bg-yellow-400 text-white" 
                              : "bg-gray-100 text-gray-400"
                          )}>
                            <Star className="h-6 w-6" />
                          </div>
                          {/* Botão Bloqueado */}
                          <div className={cn(
                            "h-12 w-12 rounded-full flex items-center justify-center shadow-md",
                            tel.bloqueado 
                              ? "bg-red-500 text-white" 
                              : "bg-white border-2 border-red-500 text-red-500"
                          )}>
                            <PhoneOff className="h-6 w-6" />
                          </div>
                          {/* Botão WhatsApp */}
                          <a
                            href={tel.whatsapp && tel.numero ? `https://wa.me/55${tel.numero.replace(/\D/g, '')}` : "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              "h-12 w-12 rounded-full flex items-center justify-center transition-colors shadow-md",
                              tel.whatsapp 
                                ? "bg-green-500 text-white hover:bg-green-600" 
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            )}
                            onClick={(e) => !tel.whatsapp && e.preventDefault()}
                          >
                            <MessageCircle className="h-6 w-6" />
                          </a>
                          {/* Botão Ligar - AZUL */}
                          <a
                            href={tel.numero ? `tel:${tel.numero.replace(/\D/g, '')}` : "#"}
                            className="h-12 w-12 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 shadow-md"
                          >
                            <Phone className="h-6 w-6" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhum telefone cadastrado</p>
                )}
              </CardContent>
            </Card>

            {/* E-mails - Layout Compacto */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">E-mails</CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                {cliente.emails?.length > 0 ? (
                  cliente.emails.map((email, index) => (
                    <div key={email.id || index} className="border-b last:border-b-0 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-64">
                          <p className="text-sm font-semibold text-slate-600 mb-2">E-mail {index + 1}</p>
                          <p className="text-lg px-3 py-2 bg-slate-50 rounded-md border truncate">{email.email || "-"}</p>
                        </div>
                        {email.senha && (
                          <div className="w-32">
                            <p className="text-sm font-semibold text-slate-600 mb-2">Senha</p>
                            <p className="text-lg font-mono px-3 py-2 bg-slate-50 rounded-md border">{email.senha}</p>
                          </div>
                        )}
                        {email.observacao && (
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-600 mb-2">Obs.:</p>
                            <p className="text-base px-3 py-2 bg-slate-50 rounded-md border">{email.observacao}</p>
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-6">
                          {/* Botão Principal */}
                          <div className={cn(
                            "h-12 w-12 rounded-full flex items-center justify-center shadow-md",
                            email.principal 
                              ? "bg-blue-500 text-white" 
                              : "bg-gray-100 text-gray-400"
                          )}>
                            <Star className="h-6 w-6" />
                          </div>
                          {/* Botão Enviar E-mail */}
                          <a
                            href={email.email ? `mailto:${email.email}` : "#"}
                            className="h-12 w-12 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-slate-900 shadow-md"
                          >
                            <Mail className="h-6 w-6" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhum e-mail cadastrado</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Profissional */}
        <TabsContent value="profissional">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Emprego */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Dados do Emprego
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cliente.emprego?.possui ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <p className="text-sm text-muted-foreground">Matrícula</p>
                      <p className="font-medium font-mono">{cliente.emprego.matricula || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Cargo</p>
                      <p className="font-medium">{cliente.emprego.cargo || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Salário</p>
                      <p className="font-medium text-green-600">{formatCurrency(cliente.emprego.salario)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Regimento</p>
                      <p className="font-medium">{cliente.emprego.regimento || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Data de Admissão</p>
                      <p className="font-medium">{formatDate(cliente.emprego.data_admissao)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Dia do Pagamento</p>
                      <p className="font-medium">{cliente.emprego.dia_pagamento || "-"}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Não possui emprego cadastrado</p>
                )}
              </CardContent>
            </Card>

            {/* Benefícios */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-5 w-5" />
                  Benefícios
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Aposentadoria */}
                <div>
                  <h4 className="font-medium mb-3">Aposentadoria</h4>
                  {cliente.beneficio?.possui_aposentadoria ? (
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5 p-3 border rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Nº do Benefício</p>
                        <p className="font-medium font-mono">{cliente.beneficio.aposentadoria_numero}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Espécie</p>
                        <p className="font-medium">{cliente.beneficio.aposentadoria_especie || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Valor</p>
                        <p className="font-medium text-green-600">{formatCurrency(cliente.beneficio.aposentadoria_valor)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Tipo de Salário</p>
                        <p className="font-medium">{cliente.beneficio.aposentadoria_tipo_salario || "Não Informado"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">UF</p>
                        <p className="font-medium">{cliente.beneficio.aposentadoria_uf || "-"}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Não possui aposentadoria</p>
                  )}
                </div>

                {/* Pensão */}
                <div>
                  <h4 className="font-medium mb-3">Pensão</h4>
                  {cliente.beneficio?.possui_pensao ? (
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5 p-3 border rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Nº do Benefício</p>
                        <p className="font-medium font-mono">{cliente.beneficio.pensao_numero}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Espécie</p>
                        <p className="font-medium">{cliente.beneficio.pensao_especie || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Valor</p>
                        <p className="font-medium text-green-600">{formatCurrency(cliente.beneficio.pensao_valor)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Tipo de Salário</p>
                        <p className="font-medium">{cliente.beneficio.pensao_tipo_salario || "Não Informado"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">UF</p>
                        <p className="font-medium">{cliente.beneficio.pensao_uf || "-"}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Não possui pensão</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Bancário */}
        <TabsContent value="bancario">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Dados para Liberação do Crédito
              </CardTitle>
              <CardDescription>Contas bancárias cadastradas para recebimento</CardDescription>
            </CardHeader>
            <CardContent>
              {cliente.contas_bancarias?.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {cliente.contas_bancarias.map((conta, index) => (
                    <div key={conta.id || index} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{conta.banco}</h4>
                          {conta.principal && <Badge className="bg-blue-100 text-blue-800">Principal</Badge>}
                        </div>
                        <Badge variant="outline">{conta.tipo_conta}</Badge>
                      </div>
                      <div className="grid gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Agência:</span>
                          <span className="font-mono">{conta.agencia}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Conta:</span>
                          <span className="font-mono">{conta.conta}</span>
                        </div>
                        {conta.variacao && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Variação:</span>
                            <span className="font-mono">{conta.variacao}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Meio de Recebimento:</span>
                          <span>{conta.meio_recebimento}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">Nenhuma conta bancária cadastrada</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Monitoramento Contrato */}
        <TabsContent value="contratos">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Landmark className="h-5 w-5" />
                    Monitoramento de Contratos
                  </CardTitle>
                  <CardDescription>Contratos do cliente - Internos e Externos</CardDescription>
                </div>
                <Link to={`/monitoramento-contratos?cpf=${cliente.cpf}`}>
                  <Button className="gap-2">
                    <FileText className="h-4 w-4" />
                    Ir para Monitoramento
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {loadingContratos ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : contratos.length > 0 ? (
                <div className="space-y-4">
                  {/* Resumo */}
                  <div className="grid gap-4 md:grid-cols-3 mb-6">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600">Total de Contratos</p>
                      <p className="text-2xl font-bold text-blue-800">{contratos.length}</p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <p className="text-sm text-orange-600">Saldo Devedor Total</p>
                      <p className="text-2xl font-bold text-orange-800">
                        {formatCurrency(contratos.reduce((sum, c) => sum + (c.saldo_devedor || 0), 0))}
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-600">Total em Parcelas</p>
                      <p className="text-2xl font-bold text-green-800">
                        {formatCurrency(contratos.reduce((sum, c) => sum + (c.valor_parcela || 0), 0))}
                      </p>
                    </div>
                  </div>

                  {/* Lista de Contratos */}
                  {contratos.map((contrato) => (
                    <div key={contrato.id} className="border rounded-lg overflow-hidden">
                      {/* Header do Contrato */}
                      <div className={cn(
                        "p-4",
                        contrato.tipo === "INTERNO" ? "bg-blue-50" : "bg-purple-50"
                      )}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "p-2 rounded-lg",
                              contrato.tipo === "INTERNO" ? "bg-blue-100" : "bg-purple-100"
                            )}>
                              <Landmark className={cn(
                                "h-5 w-5",
                                contrato.tipo === "INTERNO" ? "text-blue-600" : "text-purple-600"
                              )} />
                            </div>
                            <div>
                              <p className="font-bold">{contrato.numero_contrato}</p>
                              <p className="text-sm text-muted-foreground">{contrato.banco_nome || "Banco não informado"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{contrato.tipo}</Badge>
                            <Badge className={cn(
                              contrato.situacao === "ATIVO" ? "bg-green-100 text-green-800" :
                              contrato.situacao === "QUITADO" ? "bg-blue-100 text-blue-800" :
                              "bg-gray-100 text-gray-800"
                            )}>
                              {contrato.situacao}
                            </Badge>
                          </div>
                        </div>

                        {/* Dados do Contrato */}
                        <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
                          <div>
                            <p className="text-xs text-muted-foreground">Parcela</p>
                            <p className="font-bold">{formatCurrency(contrato.valor_parcela)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Saldo Devedor</p>
                            <p className="font-bold text-orange-600">{formatCurrency(contrato.saldo_devedor)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Parcelas</p>
                            <p className="font-medium">{contrato.parcela_atual || 0}/{contrato.parcelas_restantes || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Taxa</p>
                            <p className="font-medium">{contrato.taxa_juros ? `${contrato.taxa_juros}%` : "S/Taxa"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Início</p>
                            <p className="font-medium">{formatDate(contrato.data_inicio)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Fim</p>
                            <p className="font-medium">{formatDate(contrato.data_fim)}</p>
                          </div>
                        </div>

                        {/* Botões de Ação */}
                        <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                          <Button 
                            variant={showPortabilidade[contrato.id] ? "default" : "outline"} 
                            size="sm" 
                            className="gap-1"
                            onClick={() => {
                              setShowPortabilidade(prev => ({...prev, [contrato.id]: !prev[contrato.id]}));
                              setShowRefin(prev => ({...prev, [contrato.id]: false}));
                            }}
                          >
                            <ArrowRightLeft className="h-4 w-4" />
                            Portabilidade
                          </Button>
                          <Button 
                            variant={showRefin[contrato.id] ? "default" : "outline"} 
                            size="sm" 
                            className="gap-1"
                            onClick={() => {
                              setShowRefin(prev => ({...prev, [contrato.id]: !prev[contrato.id]}));
                              setShowPortabilidade(prev => ({...prev, [contrato.id]: false}));
                            }}
                          >
                            <RefreshCw className="h-4 w-4" />
                            Refin
                          </Button>
                        </div>
                      </div>

                      {/* Tabela de Portabilidade */}
                      {showPortabilidade[contrato.id] && (
                        <div className="border-t">
                          <div className="bg-slate-800 text-white p-3">
                            <div className="flex items-center gap-2">
                              <ArrowRightLeft className="h-5 w-5" />
                              <span className="font-bold">PORTABILIDADE</span>
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-slate-700 text-white">
                                  <th className="px-3 py-2 text-left">BANCO / SITUAÇÃO</th>
                                  <th className="px-3 py-2 text-center">PARCELAS</th>
                                  <th className="px-3 py-2 text-right">EST. SALDO DEVEDOR</th>
                                  <th className="px-3 py-2 text-center">% DESC.</th>
                                  <th className="px-3 py-2 text-right">SALDO COM DESC.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[10, 20, 30].map((desc, idx) => {
                                  const saldoBase = contrato.saldo_devedor || 0;
                                  const saldoComDesc = saldoBase * (1 - desc/100);
                                  return (
                                    <tr key={idx} className={idx % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                                      <td className="px-3 py-2">
                                        <div className="font-semibold">{contrato.banco_nome || "Banco"}</div>
                                        <div className="text-xs text-green-600">{contrato.situacao}</div>
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        {contrato.parcela_atual || 0}/{contrato.parcelas_restantes || 0}
                                      </td>
                                      <td className="px-3 py-2 text-right font-bold text-orange-600">
                                        {formatCurrency(saldoBase)}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <Badge className={desc === 20 ? "bg-green-600" : "bg-slate-500"}>
                                          {desc}%
                                        </Badge>
                                      </td>
                                      <td className="px-3 py-2 text-right font-bold text-blue-600">
                                        {formatCurrency(saldoComDesc)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Tabela de Refinanciamento */}
                      {showRefin[contrato.id] && (
                        <div className="border-t">
                          <div className="bg-emerald-800 text-white p-3">
                            <div className="flex items-center gap-2">
                              <RefreshCw className="h-5 w-5" />
                              <span className="font-bold">REFINANCIAMENTO</span>
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-emerald-700 text-white">
                                  <th className="px-3 py-2 text-left">BANCO / SITUAÇÃO</th>
                                  <th className="px-3 py-2 text-center">PARCELAS</th>
                                  <th className="px-3 py-2 text-right">SALDO DEVEDOR</th>
                                  <th className="px-3 py-2 text-center">TROCO %</th>
                                  <th className="px-3 py-2 text-right">VALOR TROCO</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[30, 50, 70].map((troco, idx) => {
                                  const saldoBase = contrato.saldo_devedor || 0;
                                  const valorTroco = saldoBase * (troco/100);
                                  return (
                                    <tr key={idx} className={idx % 2 === 0 ? "bg-emerald-50" : "bg-white"}>
                                      <td className="px-3 py-2">
                                        <div className="font-semibold">{contrato.banco_nome || "Banco"}</div>
                                        <div className="text-xs text-green-600">{contrato.situacao}</div>
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        {contrato.parcela_atual || 0}/{contrato.parcelas_restantes || 0}
                                      </td>
                                      <td className="px-3 py-2 text-right font-bold text-orange-600">
                                        {formatCurrency(saldoBase)}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <Badge className={troco === 50 ? "bg-emerald-600" : "bg-slate-500"}>
                                          {troco}%
                                        </Badge>
                                      </td>
                                      <td className="px-3 py-2 text-right font-bold text-emerald-600">
                                        {formatCurrency(valorTroco)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Landmark className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">Nenhum contrato encontrado</h3>
                  <p className="text-muted-foreground mb-4">Este cliente ainda não possui contratos cadastrados.</p>
                  <Link to={`/monitoramento-contratos?cpf=${cliente.cpf}`}>
                    <Button className="gap-2">
                      <FileText className="h-4 w-4" />
                      Cadastrar Contrato
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Observações */}
      {cliente.acoes_operacionais?.observacoes && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Observações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap">{cliente.acoes_operacionais.observacoes}</p>
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente <strong>{cliente.nome}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
